import { FC, useCallback } from 'react';

import classNames from 'classnames';
import format from 'date-fns/format';

import { formatBytes } from '../utils';

import Button from '../../Button';
import { ContentList, ContentListItem } from '../../ContentList';
import Icon from '../../Icon';
import Spinner from '../../Spinner';
import Typography from '../../Typography/Typography';

import './FileList.scss';

import { SBFile, TestMetadata } from '../../types';

export interface FileListProps {
  /** List of files */
  files: SBFile[];
  /* Disable */
  isDisabled?: boolean;
  /** Trigger delete actions */
  onDelete?: (file: SBFile) => void;
  /** Remove errored files */
  removeInvalid?: (file: SBFile) => void;
  /** Sets test id */
  testMetadata?: {
    container?: TestMetadata;
    delete?: TestMetadata;
    clearInvalid?: TestMetadata;
    download?: TestMetadata;
  };
  /* Read Only */
  isReadOnly?: boolean;
  /** Hide close button when uploading a file */
  hideClose?: boolean;
}

const FileList: FC<FileListProps> = ({
  files,
  isDisabled = false,
  onDelete,
  removeInvalid,
  testMetadata,
  isReadOnly,
  hideClose,
}) => {
  const handleOnDeleteFile = useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      file: SBFile,
    ): void => {
      e.preventDefault();
      e.stopPropagation();
      onDelete?.(file);
    },
    [onDelete],
  );

  return (
    <ContentList className="sb-file-list" {...testMetadata?.container}>
      {files.map((file: SBFile, index) => {
        const fileLoading = file.new || file.deleting;

        return (
          <ContentListItem
            key={file.id || index}
            className={classNames('sb-file-list__file', {
              'sb-file-list__file-error': file.error,
              'sb-file-list__file-loading': fileLoading,
            })}
          >
            <div className="sb-file-list__container">
              <div>
                <div className="sb-file-list__file-details">
                  <div className="sb-file-list__file-name">
                    <Icon
                      iconName="fa-file"
                      faPackage="fa-light"
                      iconType="fa"
                      className="sb-file-list__file-icon"
                    />
                    <p>{file.name}</p>
                  </div>
                </div>
                <div
                  className={classNames('sb-file-list__file-meta', {
                    'sb-file-list__file-meta-error': file.error,
                  })}
                >
                  {fileLoading ? (
                    <Typography bold variant="T15">
                      {file.new ? 'Uploading...' : 'Deleting...'}
                    </Typography>
                  ) : (
                    <>
                      {file.size && !file.error && (
                        <span className="sb-file-list__file-size">
                          {formatBytes(file.size)}
                        </span>
                      )}
                      {file.uploadedDate && !file.error && (
                        <span className="sb-file-list__file-date">
                          <span className="sb-file-list__file-size">
                            {`Uploaded ${format(
                              new Date(file.uploadedDate),
                              'MMM d, yyyy',
                            )}`}
                          </span>
                        </span>
                      )}
                      {file.error && (
                        <span>
                          <Icon
                            className="sb-file-list__file-error-icon"
                            iconName="fa-exclamation-circle"
                            faPackage="fas"
                            iconType="fa"
                          />
                          <span className="sb-file-list__file-error-message">
                            {file.message}
                          </span>
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div
                className={classNames('sb-file-list__actions', {
                  'sb-file-list__actions-error': file.error,
                  'sb-file-list__actions-loading': fileLoading,
                })}
              >
                {file.error && (
                  <Button
                    iconOnly
                    iconType="fa-xmark-large"
                    iconPackage="fas"
                    className="sb-file-list__action sb-file-list__remove-invalid"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeInvalid?.(file);
                    }}
                    styleType="link"
                    {...testMetadata?.clearInvalid}
                  />
                )}
                {fileLoading && !file.error ? (
                  <>
                    <Spinner size="medium" />
                    {!hideClose && file.new && (
                      <Button
                        iconOnly
                        iconType="fa-xmark-large"
                        iconPackage="fas"
                        className="sb-file-list__action"
                        onClick={(e) => handleOnDeleteFile(e, file)}
                        styleType="link"
                        {...testMetadata?.delete}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {!file.error && file.url && !fileLoading && (
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noreferrer"
                        download
                        type="application/octet-stream"
                        {...testMetadata?.download}
                      >
                        <Icon
                          iconName="fa-cloud-arrow-down"
                          faPackage="fa-light"
                          iconType="fa"
                          className="sb-file-list__action sb-file-list__icon"
                          title="Download"
                        />
                      </a>
                    )}
                    {!file.error &&
                      !fileLoading &&
                      !isReadOnly &&
                      onDelete !== undefined && (
                        <Button
                          iconOnly
                          iconType="fa-trash-can"
                          iconPackage="fa-light"
                          className="sb-file-list__action sb-file-list__icon"
                          onClick={(e) => handleOnDeleteFile(e, file)}
                          styleType="link"
                          {...testMetadata?.delete}
                          labelText="Delete"
                          disabled={file.isReadOnly || isDisabled}
                        />
                      )}
                  </>
                )}
              </div>
            </div>
          </ContentListItem>
        );
      })}
    </ContentList>
  );
};

export default FileList;
